/**
 * L-NAV-9 — UNE PERMISSION PAR ÉCRAN, LUE AUX TROIS ÉTAGES.
 *
 * Ce catalogue est la SEULE source de la condition d'accès d'un écran côté
 * client : le menu (`components/sidebarNav.ts`) et la route
 * (`<ProtectedRoute screen="…">` dans `App.tsx`) lisent la même entrée, donc
 * ne peuvent plus diverger. Le troisième étage (serveur) est la garde du
 * contrôleur qui porte la même permission (voir la PR L-NAV-9, tableau).
 *
 * Règles :
 *  - `permission` : un drapeau (ou PLUSIEURS : l'un d'eux suffit) lu via
 *    `hasPermission`, qui applique déjà la compat (`access/legacyFallbacks`) ;
 *  - `roles` : rôles admis en PLUS du drapeau (Aujourd'hui : ADMIN) ;
 *    `denyRoles` : rôles exclus (Campagnes : pas les agents — décision produit
 *    du menu, inchangée — la LISTE seulement : la fiche `/campaigns/:id` est
 *    l'écran `campaignDetail`, ouvert aux agents affectés, #451) ;
 *  - `systemAdminOnly` : écrans du groupe « Système » ;
 *  - le SUPER-ADMIN (ADMIN du tenant Système) passe PARTOUT, sans dépendre
 *    d'un drapeau (décision client) — comme `PermissionsGuard` côté serveur.
 *  - `route: 'open'` : la route n'est PAS gardée (Contacts : la fiche est
 *    ouverte depuis le téléphone par des agents sans l'entrée de menu —
 *    16/55 en prod ; Paramètres : pour tous).
 */
import type { PermissionKey } from '../types/permissions'

export type Role = 'ADMIN' | 'AGENT' | 'USER'

export type ScreenId =
  | 'today'
  | 'softphone'
  | 'contacts'
  | 'import'
  | 'campaigns'
  | 'campaignDetail'
  | 'smsCampaigns'
  | 'myCampaigns'
  | 'history'
  | 'analytics'
  | 'team'
  | 'templates'
  | 'settings'
  | 'tenants'
  | 'twilioNumbers'
  | 'providers'

export interface ScreenRule {
  /** Chemins de `App.tsx` que l'écran couvre (documentation + garde de source). */
  paths: readonly string[]
  /** Un drapeau suffit parmi ceux listés. Absent = pas de drapeau requis. */
  permission?: readonly PermissionKey[]
  roles?: readonly Role[]
  denyRoles?: readonly Role[]
  systemAdminOnly?: boolean
  /** `guarded` : `<ProtectedRoute screen>` dans App.tsx ; `open` : route libre (motif ci-dessus). */
  route: 'guarded' | 'open'
  /**
   * #451 — `false` : écran SANS entrée de menu propre (on y arrive depuis un
   * autre écran : la fiche d'une campagne depuis sa liste). Absent : l'écran
   * a son entrée (garde de `screens.spec.ts`).
   */
  menu?: false
}

export const SCREENS: Readonly<Record<ScreenId, ScreenRule>> = {
  // Aujourd'hui (L-NAV-8, intégration 3B) : `/accueil` remplace `/supervision` (redirection).
  today: { paths: ['/accueil'], permission: ['canAccessStats'], roles: ['ADMIN'], route: 'guarded' },
  // `/v2/softphone` : route gardée par `requiredPermission="accessDialer"` (= canAccessCalls), zone téléphonie.
  softphone: { paths: ['/v2/softphone'], permission: ['canAccessCalls'], route: 'open' },
  contacts: { paths: ['/contacts', '/contacts/:contactId/timeline'], permission: ['canAccessContacts'], route: 'open' },
  // `/import` : route gardée par `requiredPermission="canAccessImport"` (texte exact exigé par importRoutes.guard.spec).
  import: { paths: ['/import'], permission: ['canAccessImport'], route: 'open' },
  // La LISTE `/campaigns` : pas les agents (décision produit du menu, inchangée).
  campaigns: { paths: ['/campaigns'], permission: ['canAccessCampaigns'], denyRoles: ['AGENT'], route: 'guarded' },
  // #451 (planche 15) — la FICHE `/campaigns/:id` s'ouvre aussi aux agents, en
  // lecture seule ; le SERVEUR ne leur sert que les campagnes qui leur sont
  // affectées (403 `CAMPAIGN_NOT_ASSIGNED` sinon). On y arrive depuis une liste.
  campaignDetail: { paths: ['/campaigns/:id'], permission: ['canAccessCampaigns'], route: 'guarded', menu: false },
  // SMS-CAMPAGNE-2 — campagnes SMS : liste (onglet SMS de Campagnes), nouvelle
  // campagne, suivi. ADMIN du tenant seulement (décision client 3 ; le serveur
  // pose `@Roles(ADMIN)` sur tout `/sms-campaigns`) ; le compte système passe
  // et désigne l'organisation (`?org=`). Pas d'entrée de menu propre : on y
  // arrive par l'onglet SMS de la page Campagnes (l'entrée « Campagnes » du
  // menu couvre déjà `/campaigns/…`).
  smsCampaigns: {
    paths: ['/campaigns/sms', '/campaigns/sms/new', '/campaigns/sms/:id'],
    permission: ['canAccessCampaigns'],
    roles: ['ADMIN'],
    route: 'guarded',
    menu: false,
  },
  // #451 — « Mes campagnes » : la liste de l'AGENT, réduite aux campagnes où il
  // est affecté (`GET /campaigns/my-campaigns`) ; son entrée « Campagnes » du menu.
  myCampaigns: { paths: ['/mes-campagnes'], permission: ['canAccessCampaigns'], roles: ['AGENT'], route: 'guarded' },
  history: { paths: ['/historique'], permission: ['canAccessCalls'], route: 'guarded' },
  analytics: { paths: ['/analytics'], permission: ['canAccessStats'], route: 'guarded' },
  // Équipe (L-NAV-7, intégration 3B) : liste + fiche d'une personne. Le menu lit le
  // catalogue = UNION des deux anciennes entrées (Utilisateurs : canAccessUsers ;
  // Permissions : canManagePermissions — page supprimée, `/admin/permissions`
  // redirige ici). La route garde `requiredRole="ADMIN"` (L-NAV-7) ; serveur :
  // `GET /users` canViewUsers, section Accès canManagePermissions.
  team: { paths: ['/equipe', '/equipe/:userId'], permission: ['canAccessUsers', 'canManagePermissions'], route: 'open' },
  templates: { paths: ['/messaging/templates'], permission: ['canAccessSmsTemplates', 'canAccessEmailTemplates'], route: 'guarded' },
  settings: { paths: ['/settings'], route: 'open' },
  tenants: { paths: ['/admin/tenants', '/admin/tenants/:id'], systemAdminOnly: true, route: 'guarded' },
  twilioNumbers: { paths: ['/admin/twilio-numbers'], systemAdminOnly: true, route: 'guarded' },
  providers: { paths: ['/messaging/providers'], systemAdminOnly: true, route: 'guarded' },
}

export interface AccessContext {
  role: string | undefined
  isSystemAdmin: boolean
  /** Lecteur de drapeaux — celui de `usePermissions` (compat + super-admin inclus). */
  hasPermission: (permission: PermissionKey) => boolean
}

/** L'écran est-il permis à ce compte ? */
export function canAccessScreen(screen: ScreenId, ctx: AccessContext): boolean {
  if (ctx.isSystemAdmin) return true
  const rule = SCREENS[screen]
  if (rule.systemAdminOnly) return false
  if (rule.roles && !rule.roles.includes(ctx.role as Role)) return false
  if (rule.denyRoles && rule.denyRoles.includes(ctx.role as Role)) return false
  if (rule.permission && !rule.permission.some((p) => ctx.hasPermission(p))) return false
  return true
}
